// src/pages/sqf/SqfContractorsPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import sqfService from '../../services/sqfService';
import { useLanguage, t } from '../../context/LanguageContext';
import SqfNav from '../../components/SqfNav';

const AGREEMENT_BADGE = {
  valid:    { label: 'Valid',    color: 'success' },
  expiring: { label: 'Expiring', color: 'warning' },
  expired:  { label: 'Expired',  color: 'danger'  },
  none:     { label: 'No agreement', color: 'secondary' },
};

const EMPTY_FORM = { name: '', company: '', contractorType: '', email: '', phone: '', notes: '', policyId: '' };
const TYPES = ['Pest control', 'Electrician', 'Plumber', 'Refrigeration', 'Equipment repair', 'Cleaning', 'Other'];

export default function SqfContractorsPage() {
  const { lang } = useLanguage();
  const [contractors, setContractors] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // New / edit contractor modal
  const [showModal, setShowModal] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Signing modal
  const [signingContractor, setSigningContractor] = useState(null);
  const [signingPolicy, setSigningPolicy] = useState(null);   // { ...policy, activeVersion }
  const [signedAt, setSignedAt] = useState('');
  const [signatureName, setSignatureName] = useState('');
  const [signingStep, setSigningStep] = useState('read');
  const [savingAgreement, setSavingAgreement] = useState(false);

  // View signed agreement
  const [viewContractor, setViewContractor] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      sqfService.getContractors(),
      sqfService.getPolicies(),
    ])
      .then(([c, p]) => {
        setContractors(c);
        setPolicies(p.filter(pol => !pol.archivedAt && pol.activeVersionId));
      })
      .catch(() => setError(t('Failed to load contractors.', 'Error al cargar contratistas.', lang)))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const openNewModal = () => {
    setEditingContractor(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEditModal = (c) => {
    setEditingContractor(c);
    setForm({
      name: c.name || '',
      company: c.company || '',
      contractorType: c.contractorType || '',
      email: c.email || '',
      phone: c.phone || '',
      notes: c.notes || '',
      policyId: c.policyId ? String(c.policyId) : '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const data = { ...form, policyId: form.policyId ? Number(form.policyId) : null };
      if (editingContractor) {
        await sqfService.updateContractor(editingContractor.id, data);
      } else {
        await sqfService.createContractor(data);
      }
      setShowModal(false);
      load();
    } catch {
      setError(t('Failed to save contractor.', 'Error al guardar contratista.', lang));
    } finally {
      setSaving(false);
    }
  };

  const openSigningModal = async (c) => {
    if (!c.policyId) {
      setError(t(
        'No policy linked to this contractor. Edit the contractor to link a policy first.',
        'No hay política vinculada a este contratista. Edite el contratista para vincular una política primero.',
        lang
      ));
      return;
    }
    try {
      const fullPolicy = await sqfService.getPolicy(c.policyId);
      const activeVersion = (fullPolicy.Versions || []).find(v => v.id === fullPolicy.activeVersionId);
      if (!activeVersion) {
        setError(t(
          'The linked policy has no published version yet. Publish a version in the Policies section first.',
          'La política vinculada no tiene versión publicada. Publique una versión en la sección de Políticas primero.',
          lang
        ));
        return;
      }
      setSigningPolicy({ ...fullPolicy, activeVersion });
      setSigningContractor(c);
      setSignedAt(new Date().toISOString().split('T')[0]);
      setSignatureName('');
      setSigningStep('read');
      setSavingAgreement(false);
    } catch {
      setError(t('Failed to load policy for signing.', 'Error al cargar la política para firmar.', lang));
    }
  };

  const handleSign = async () => {
    if (!signatureName.trim() || !signingPolicy?.activeVersion) return;
    setSavingAgreement(true);
    try {
      await sqfService.updateContractorAgreement(signingContractor.id, {
        signedAt,
        signatureName,
        policyVersionId: signingPolicy.activeVersion.id,
      });
      setSigningContractor(null);
      setSigningPolicy(null);
      load();
    } catch {
      setError(t('Failed to save agreement.', 'Error al guardar el acuerdo.', lang));
    } finally {
      setSavingAgreement(false);
    }
  };

  const publishedPolicies = policies; // already filtered to active + published

  return (
    <>
      <SqfNav />
      <div className="container mt-4 pb-5">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <Link to="/sqf/templates" className="text-muted" style={{ fontSize: 12 }}>← {t('Templates', 'Plantillas', lang)}</Link>
          <h4 className="mb-0 mt-1">{t('Contractors', 'Contratistas', lang)}</h4>
        </div>
        <div className="d-flex gap-2">
          <Link to="/sqf/visitors" className="btn btn-outline-secondary btn-sm">{t('Visitor Log', 'Registro de Visitantes', lang)}</Link>
          <Link to="/sqf/policies" className="btn btn-outline-secondary btn-sm">{t('Policies', 'Políticas', lang)}</Link>
          <button className="btn btn-primary btn-sm" onClick={openNewModal}>+ {t('Add Contractor', 'Agregar Contratista', lang)}</button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}<button className="btn-close float-end btn-sm" onClick={() => setError('')} /></div>}

      {/* Expiry alerts */}
      {contractors.filter(c => ['expired', 'expiring'].includes(c.agreementStatus)).length > 0 && (
        <div className="alert alert-warning py-2 mb-3">
          <strong>{t('Agreement attention required:', 'Atención requerida en acuerdos:', lang)}</strong>{' '}
          {contractors.filter(c => ['expired', 'expiring'].includes(c.agreementStatus)).map((c, i) => (
            <span key={c.id}>{i > 0 && ', '}{c.name} ({c.agreementStatus})</span>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center py-5 text-muted"><div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}</div>
      ) : contractors.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <div style={{ fontSize: 40 }}>🏗</div>
          <p className="mt-2">{t('No contractors yet.', 'Aún no hay contratistas.', lang)}</p>
          <button className="btn btn-primary btn-sm" onClick={openNewModal}>+ {t('Add Contractor', 'Agregar Contratista', lang)}</button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-bordered table-hover">
            <thead className="table-light">
              <tr>
                <th>{t('Name', 'Nombre', lang)}</th>
                <th>{t('Company', 'Empresa', lang)}</th>
                <th>{t('Type', 'Tipo', lang)}</th>
                <th>{t('Policy', 'Política', lang)}</th>
                <th>{t('Agreement', 'Acuerdo', lang)}</th>
                <th>{t('Signed by', 'Firmado por', lang)}</th>
                <th>{t('Expires', 'Vence', lang)}</th>
                <th style={{ width: 160 }}></th>
              </tr>
            </thead>
            <tbody>
              {contractors.map(c => {
                const badge = AGREEMENT_BADGE[c.agreementStatus] || AGREEMENT_BADGE.none;
                const badgeLabel = c.agreementStatus === 'valid'    ? t('Valid',         'Válido',          lang)
                                 : c.agreementStatus === 'expiring' ? t('Expiring',      'Por vencer',      lang)
                                 : c.agreementStatus === 'expired'  ? t('Expired',       'Vencido',         lang)
                                                                     : t('No agreement', 'Sin acuerdo',     lang);
                return (
                  <tr key={c.id}>
                    <td className="fw-semibold" style={{ fontSize: 13 }}>{c.name}</td>
                    <td style={{ fontSize: 13 }}>{c.company || '—'}</td>
                    <td style={{ fontSize: 12 }}>{c.contractorType || '—'}</td>
                    <td style={{ fontSize: 12 }}>
                      {c.Policy
                        ? <Link to={`/sqf/policies/${c.Policy.id}/edit`} className="text-decoration-none">{c.Policy.name}</Link>
                        : <span className="text-muted fst-italic">{t('None', 'Ninguna', lang)}</span>
                      }
                    </td>
                    <td><span className={`badge bg-${badge.color}`}>{badgeLabel}</span></td>
                    <td style={{ fontSize: 12 }}>
                      {c.signatureName
                        ? <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{c.signatureName}</span>
                        : '—'}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {c.agreementExpiresAt ? new Date(c.agreementExpiresAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => openEditModal(c)}>{t('Edit', 'Editar', lang)}</button>
                      <button className="btn btn-outline-primary btn-sm me-1" onClick={() => openSigningModal(c)}
                        disabled={!c.policyId}>
                        {c.agreementStatus === 'none' ? t('Sign', 'Firmar', lang) : t('Renew', 'Renovar', lang)}
                      </button>
                      {c.signatureName && (
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => setViewContractor(c)}>{t('View', 'Ver', lang)}</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── New / Edit contractor modal ── */}
      {showModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingContractor ? t('Edit Contractor', 'Editar Contratista', lang) : t('New Contractor', 'Nuevo Contratista', lang)}</h5>
                <button className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                {[
                  ['name',    t('Name *',           'Nombre *',              lang)],
                  ['company', t('Company / Vendor',  'Empresa / Proveedor',   lang)],
                  ['email',   t('Email',             'Correo electrónico',    lang)],
                  ['phone',   t('Phone',             'Teléfono',              lang)],
                ].map(([key, label]) => (
                  <div className="mb-2" key={key}>
                    <label className="form-label mb-1 fw-semibold" style={{ fontSize: 13 }}>{label}</label>
                    <input className="form-control form-control-sm" value={form[key]} onChange={e => setField(key, e.target.value)} />
                  </div>
                ))}
                <div className="mb-2">
                  <label className="form-label mb-1 fw-semibold" style={{ fontSize: 13 }}>{t('Type', 'Tipo', lang)}</label>
                  <select className="form-select form-select-sm" value={form.contractorType} onChange={e => setField('contractorType', e.target.value)}>
                    <option value="">{t('Select…', 'Seleccionar…', lang)}</option>
                    {TYPES.map(tp => <option key={tp} value={tp}>{tp}</option>)}
                  </select>
                </div>
                <div className="mb-2">
                  <label className="form-label mb-1 fw-semibold" style={{ fontSize: 13 }}>{t('Agreement Policy', 'Política de Acuerdo', lang)}</label>
                  <select className="form-select form-select-sm" value={form.policyId} onChange={e => setField('policyId', e.target.value)}>
                    <option value="">{t('None', 'Ninguna', lang)}</option>
                    {publishedPolicies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  {publishedPolicies.length === 0 && (
                    <div className="form-text">
                      {t('No published policies yet.', 'Aún no hay políticas publicadas.', lang)} <Link to="/sqf/policies">{t('Create one →', 'Crear una →', lang)}</Link>
                    </div>
                  )}
                </div>
                <div className="mb-2">
                  <label className="form-label mb-1 fw-semibold" style={{ fontSize: 13 }}>{t('Notes', 'Notas', lang)}</label>
                  <textarea className="form-control form-control-sm" rows={2} value={form.notes} onChange={e => setField('notes', e.target.value)} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>{t('Cancel', 'Cancelar', lang)}</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
                  {saving ? t('Saving…', 'Guardando…', lang) : editingContractor ? t('Save Changes', 'Guardar Cambios', lang) : t('Add Contractor', 'Agregar Contratista', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Signing modal ── */}
      {signingContractor && signingPolicy && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-0">{signingPolicy.name}</h5>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {signingContractor.name}{signingContractor.company ? ` · ${signingContractor.company}` : ''}
                    {' · '}v{signingPolicy.activeVersion.versionNumber}
                    {signingPolicy.activeVersion.revisionDate ? ` (${signingPolicy.activeVersion.revisionDate})` : ''}
                  </div>
                </div>
                <button className="btn-close" onClick={() => setSigningContractor(null)} />
              </div>

              {signingStep === 'read' ? (
                <>
                  <div className="modal-body">
                    <div
                      className="border rounded p-3"
                      style={{ maxHeight: 460, overflowY: 'auto' }}
                      dangerouslySetInnerHTML={{ __html: signingPolicy.activeVersion.content || `<p class="text-muted">${t('No content.', 'Sin contenido.', lang)}</p>` }}
                    />
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setSigningContractor(null)}>{t('Cancel', 'Cancelar', lang)}</button>
                    <button className="btn btn-primary" onClick={() => setSigningStep('sign')}>
                      {t('I Have Read the Agreement →', 'He leído el acuerdo →', lang)}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="modal-body">
                    <button className="btn btn-link btn-sm p-0 mb-3 text-muted" style={{ fontSize: 12 }} onClick={() => setSigningStep('read')}>
                      ← {t('Re-read agreement', 'Releer el acuerdo', lang)}
                    </button>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">{t('Date', 'Fecha', lang)}</label>
                      <input type="date" className="form-control" value={signedAt} onChange={e => setSignedAt(e.target.value)} />
                      <div className="form-text">{t('Agreement expires 1 year from this date.', 'El acuerdo vence 1 año a partir de esta fecha.', lang)}</div>
                    </div>

                    <div className="mb-1">
                      <label className="form-label fw-semibold">{t('Electronic Signature — Type your full legal name', 'Firma electrónica — Escriba su nombre legal completo', lang)}</label>
                      <input
                        className="form-control"
                        style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 18 }}
                        placeholder={t('Full name…', 'Nombre completo…', lang)}
                        value={signatureName}
                        onChange={e => setSignatureName(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="text-muted mb-3" style={{ fontSize: 12 }}>
                      {t(
                        'By typing your name above, you acknowledge that you have read and agree to the terms of the agreement.',
                        'Al escribir su nombre, reconoce que ha leído y acepta los términos del acuerdo.',
                        lang
                      )}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setSigningContractor(null)}>{t('Cancel', 'Cancelar', lang)}</button>
                    <button className="btn btn-success" onClick={handleSign}
                      disabled={savingAgreement || !signatureName.trim() || !signedAt}>
                      {savingAgreement ? t('Saving…', 'Guardando…', lang) : t('✓ Sign & Save', '✓ Firmar y Guardar', lang)}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── View signed agreement modal ── */}
      {viewContractor && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-0">{t('Signed Agreement', 'Acuerdo Firmado', lang)}</h5>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {viewContractor.name}{viewContractor.company ? ` · ${viewContractor.company}` : ''}
                  </div>
                </div>
                <button className="btn-close" onClick={() => setViewContractor(null)} />
              </div>
              <div className="modal-body">
                {viewContractor.SignedVersion?.content ? (
                  <div
                    className="border rounded p-3 mb-4"
                    style={{ maxHeight: 320, overflowY: 'auto' }}
                    dangerouslySetInnerHTML={{ __html: viewContractor.SignedVersion.content }}
                  />
                ) : (
                  <div className="text-muted mb-4" style={{ fontSize: 13 }}>{t('Policy text not available for this signature record.', 'Texto de política no disponible para este registro de firma.', lang)}</div>
                )}

                <div className="border rounded p-3 bg-light">
                  <div className="text-muted mb-1" style={{ fontSize: 12 }}>{t('Electronic Signature', 'Firma Electrónica', lang)}</div>
                  <div style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 22, borderBottom: '1px solid #aaa', paddingBottom: 4, marginBottom: 8 }}>
                    {viewContractor.signatureName}
                  </div>
                  <div className="d-flex gap-4 flex-wrap" style={{ fontSize: 12, color: '#555' }}>
                    <span><strong>{t('Policy:', 'Política:', lang)}</strong> {viewContractor.Policy?.name || '—'}</span>
                    <span><strong>{t('Version:', 'Versión:', lang)}</strong> {viewContractor.SignedVersion ? `v${viewContractor.SignedVersion.versionNumber}` : '—'}</span>
                    <span><strong>{t('Signed:', 'Firmado:', lang)}</strong> {viewContractor.agreementSignedAt ? new Date(viewContractor.agreementSignedAt).toLocaleDateString() : '—'}</span>
                    <span><strong>{t('Expires:', 'Vence:', lang)}</strong> {viewContractor.agreementExpiresAt ? new Date(viewContractor.agreementExpiresAt).toLocaleDateString() : '—'}</span>
                    {viewContractor.signatureIp && <span><strong>IP:</strong> {viewContractor.signatureIp}</span>}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setViewContractor(null)}>{t('Close', 'Cerrar', lang)}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
